import { DataService } from './../../service/data.service';
import { Subscription } from 'rxjs';
import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LenderConfiguration } from '@shared/model/lender-config.model';

import { LoginWidgetContextData, OIDLoginWidgetExportData } from '@cudirect-widgets/ea-login';
import { AlertInfo } from '@cudirect-widgets/ea-login/lib/models/alert-info.type'; // TODO: Should not use deep imports.
import { ThemeService, DictionaryService, AppConfigService as ConfigService, TokenService, JwtToken } from '@cudirect/shared-ui-library';

import { AppConfigService } from '@app/config/config.service';
import { AppConfig } from '@app/config/config.type';
import { DOCUMENT } from '@angular/common';
import { UserService } from '@workflow/ecp-shared/service/user/user.service';
import { UserInfo } from '@workflow/ecp-shared/model/userinfo';
import { LenderService } from '@shared/service/lender/lender.service';
import { XApiKeyService } from '@shared/service/x-api-key.service';
import { AuditService } from 'src/admin/admin-portal/component/audit/services/audit.service';

@Component({
  selector: 'cu-admin-login',
  styleUrls: ['./admin-login.component.scss'],
  templateUrl: './admin-login.component.html'
})
export class AdminLoginComponent implements OnInit, OnDestroy {
  public dataReady = false;
  public lenderConfig: LenderConfiguration;
  /**
   * The CUDL SSO Application alias
   */
  private readonly applicationName = 'CUDL.LOS';
  /**
   * The Admin Portal Root Route
   */
  private readonly adminURL = '/admin';

  /**
   * The Admin Portal login URL
   */
  private readonly adminLoginURL = `${this.adminURL}/login`;

  /**
   * Show spinner on Page Load
   */
  public showSpinner = false;
  /**
   * After login, display a message during the final redirect
   */
  public loginCompleting = false;

  public appConfigData: AppConfig;

  // TODO: All instances of this widget context can be refactored to store the static parts in 1 location,
  // then grab that and modify as needed. For now, I've followed the pattern that already exists,
  // but this should be refactored.
  public widgetContext: LoginWidgetContextData = {
    loginFlow: {},
    createNewFlow: {
      enabled: false
    },
    config: {
      appInsightKey: '',
      dictionaryPath: null
    },
    oid: {
      apiBaseURL: this.configService?.settings?.oid.apiBaseURL,
      oidConfigName: this.configService?.settings?.oid.adminPortalOidConfigName
    },
    tokenReplacements: {}
  };
  private elRef: HTMLBodyElement;
  private subscriptions: Subscription = new Subscription();
  public isReady = false;
  private secondSub: Subscription;

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly lenderService: LenderService,
    public readonly userService: UserService,
    public readonly tokenService: TokenService,
    private readonly appConfigService: AppConfigService,
    private themeService: ThemeService,
    @Inject(DOCUMENT) public document: Document,
    private dataService: DataService,
    private configService: ConfigService,
    private dictionary: DictionaryService,
    private readonly xApiKeyService: XApiKeyService,
    private readonly auditService: AuditService
  ) {
    this.tokenService.initialize(
      this.configService?.settings?.oid.apiBaseURL,
      this.xApiKeyService.createApiHeader(),
      this.configService?.settings?.oid.adminPortalOidConfigName
    );
    this.tokenService.configureCache(
      this.configService?.settings?.adminPortalTokenStorageType || 'sessionStorage',
      this.configService?.settings?.adminPortalCachedTokenName || 'admin-portal-user-session'
    );
    this.elRef = this.document.getElementsByTagName('body')[0];
    const dictionarySub = this.dataService.dictionaryIsReady().subscribe((res) => {
      if (res) {
        this.secondSub = this.dictionary.onLoad.subscribe(() => {
          // this.secondSub.unsubscribe();
          this.dataReady = true;
        });
        this.subscriptions.add(this.secondSub);

        this.dictionary.updateJSON({
          'ea-authenticate-oid-login': {
            title: 'arc DX Admin Portal',
            subtitle: 'Please login to administer ' + this.lenderService?.lenderData?.lenderName + ' Credit Union'
          }
        });

        const routeSub = this.route.queryParams.subscribe((params) => {
          const action = params.action;
          if (action) {
            this.handleQueryParamSubs(action);
          }
        });
        this.subscriptions.add(routeSub);
      }
    });
    this.subscriptions.add(dictionarySub);
  }

  private handleQueryParamSubs(action: string) {
    // Handles the various states from the timeout component.
    const newURL = location.href.split('?')[0];
    window.history.pushState('object', document.title, newURL);
    let alert: AlertInfo;
    switch (action) {
      case 'timeout':
        alert = {
          message: this.dictionary.read('ea-authenticate-admin-login/errors/inactivity/detail'),
          type: 'warning',
          heading: this.dictionary.read('ea-authenticate-admin-login/errors/inactivity/title')
        };
        break;
      case 'invalid':
        alert = {
          message: this.dictionary.read('ea-authenticate-admin-login/errors/invalid/detail'),
          type: 'warning',
          heading: this.dictionary.read('ea-authenticate-admin-login/errors/invalid/title')
        };
        break;
      case 'expired':
        alert = {
          message: this.dictionary.read('ea-authenticate-admin-login/errors/expired/detail'),
          type: 'warning',
          heading: this.dictionary.read('ea-authenticate-admin-login/errors/expired/title')
        };
        break;
      case 'no-access':
        alert = {
          message: this.dictionary.read('ea-authenticate-admin-login/errors/noAccess/detail'),
          type: 'warning',
          heading: this.dictionary.read('ea-authenticate-admin-login/errors/noAccess/title')
        };
        break;
      case 'appNotInSSO':
        alert = {
          message: this.dictionary.read('ea-authenticate-admin-login/errors/noSSOAccess/detail'),
          type: 'warning',
          heading: this.dictionary.read('ea-authenticate-admin-login/errors/noSSOAccess/title')
        };
        break;
      case 'noAppParts':
        alert = {
          message: this.dictionary.read('ea-authenticate-admin-login/errors/noSSOPermissions/detail'),
          type: 'warning',
          heading: this.dictionary.read('ea-authenticate-admin-login/errors/noSSOPermissions/title')
        };
        break;
      default:
        alert = null;
    }

    if (alert) {
      this.widgetContext.setAlert = alert;
      this.widgetContext = { ...this.widgetContext };
    }
  }

  public ngOnInit(): void {
    this.document.getElementsByTagName('main')[0]?.classList.add('bl-bg-color--gradient-primary');
    const lenderPhone = this.getLenderPhone();
    this.appConfigData = this.appConfigService.getData();

    this.lenderConfig = this.lenderService.lenderDataInfo;
    this.themeService.setTheme('#445663', this.lenderConfig?.adaLevel, this.lenderConfig?.lenderLogoUrl);
    // todo : find an intelligent way for dictionary not to override each other
    this.widgetContext.tokenReplacements = {
      'admin-name': `<strong>${this.lenderConfig?.lenderName}</strong>`,
      'admin-phone': `<a href="tel:${lenderPhone}" id="admin-phone">${lenderPhone}</a>`,
      'admin-email': `<a href="mailto:${this.lenderConfig?.lenderEmail}" id="admin-email">${this.lenderConfig?.lenderEmail}</a>`
    };
  }

  // TODO: This type of function could live in its own utilities file or in a service.
  // It could accept the lender config as an argument and get called when needed instead of repeated. DRY.
  public getLenderPhone() {
    if (this.lenderConfig && this.lenderConfig.contacts) {
      // Get first contact with type phone
      const firstPhone = this.lenderConfig.contacts.filter((contact) => contact.type.toLowerCase() === 'phone')[0];
      if (!firstPhone) {
        return;
      }
      const phoneExt = firstPhone.additionalValue ? ', Ext. ' + firstPhone.additionalValue : '';
      const phoneDesc = firstPhone.description ? ' (' + firstPhone.description + ')' : '';
      const phoneContact = firstPhone.value + phoneExt + phoneDesc;
      return phoneContact;
    }
  }

  /**
   * When the login widget completes, its data comes here.
   * @param data The login widget export data.
   */
  public receivedDataHandler(data: OIDLoginWidgetExportData | string): void {
    data = data as OIDLoginWidgetExportData;
    const userInfo: UserInfo = new UserInfo();
    userInfo.isAdminPortalLogin = true;
    this.userService.user = userInfo;

    // Removed the 'SuperAdmin' role check and any role check as Admin Portal
    // is now protected with permissions on all routes via AppPartNameAliases from CUDL SSO
    // Adding a role check here would essentiall hard code entrance to admin portal and prevent
    // the business from add new roles on the fly
    this.tokenService.updateToken(data.token as JwtToken);
    this.dataReady = false;
    this.loginCompleting = true;
    this.auditService.auditLogin().subscribe();
    this.router.navigate([this.adminURL]);
  }

  /**
   * Called when the timeout component emits the logout event.
   * Passes that event to the login page for messsaging.
   */
  public handleLogout(type: string): void {
    // TODO: For some reason en error in the getUserMetadata() causes a redirect to localhost:4200, not sure why
    this.router.navigate([this.adminLoginURL], {
      relativeTo: this.route,
      queryParams: { action: type }
    });
  }

  /**
   * Destroy the component
   */
  public ngOnDestroy(): void {
    if (this.dictionary) {
      this.dictionary.updateJSON({
        'ea-authenticate-oid-login': {
          title: 'Welcome back',
          subtitle: ''
        }
      });
    }

    if (this.themeService) {
      this.themeService.setTheme(this.lenderConfig?.mainColor, this.lenderConfig?.adaLevel, this.lenderConfig?.lenderLogoUrl);
    }

    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }
}
